import { useNavigate } from "@tanstack/solid-router";
import { createEffect, createMemo, createResource, createSignal, For, Show } from "solid-js";
import {
  everClaimedTicket,
  fetchConnectEvents,
  fetchMe,
  fetchMemberProfile,
  fetchMeetupNights,
  fetchProfile,
  hasDraftProfile,
  hasMemberProfileContent,
  isProfileOnboardingComplete,
  type ConnectEvent,
} from "~/lib/api";
import { meetupSlugFromLocation } from "~/lib/meetup";
import { formatEventDateLabel, partitionConnectEvents } from "~/lib/events";
import { resolveEventHint } from "~/lib/event-hint";

function EventRow(props: {
  event: ConnectEvent;
  featured?: boolean;
  status?: string;
  onSelect: (event: ConnectEvent) => void;
}) {
  const dateLabel = () => formatEventDateLabel(props.event);
  return (
    <li>
      <button
        type="button"
        class={`event-pill event-pill-btn${props.featured ? " event-pill--featured" : ""}`}
        onClick={() => props.onSelect(props.event)}
      >
        <div>
          <Show when={props.event.city}>
            <span class={`city-pill${props.featured ? "" : " city-pill--muted"}`}>
              {props.event.city}
            </span>
          </Show>
          <strong>{props.event.name}</strong>
          <span>
            {dateLabel() ?? props.event.city ?? props.event.ticketing_event_id}
            <Show when={dateLabel() && props.event.city}>
              {" · "}
              {props.event.city}
            </Show>
          </span>
        </div>
        <span class="status-chip">{props.status ?? (props.featured ? "Next" : "Open")}</span>
      </button>
    </li>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const [me] = createResource(fetchMe);
  const [events] = createResource(fetchConnectEvents);
  const [memberProfile] = createResource(fetchMemberProfile);
  const [meetupNights] = createResource(fetchMeetupNights);
  const pass = () => everClaimedTicket(memberProfile());
  const [deepLinkHandled, setDeepLinkHandled] = createSignal(false);
  const [pastOpen, setPastOpen] = createSignal(false);

  const partitioned = createMemo(() => partitionConnectEvents(events()?.data ?? []));

  async function onSelectEvent(event: ConnectEvent) {
    const cityDirectory = () =>
      navigate({
        to: "/directory/$eventId",
        params: { eventId: event.ticketing_event_id },
      });

    try {
      const profile = await fetchProfile(event.ticketing_event_id);
      if (isProfileOnboardingComplete(profile.data)) {
        cityDirectory();
        return;
      }
      if (everClaimedTicket(profile) && event.connect_open === false) {
        cityDirectory();
        return;
      }
      if (hasDraftProfile(profile.data)) {
        navigate({
          to: "/me/edit",
          search: { fromEvent: event.ticketing_event_id },
        });
        return;
      }
      if (event.connect_open === false) {
        cityDirectory();
        return;
      }
    } catch {
      // Fall through to onboarding if profile fetch fails
    }
    if (event.connect_open === false) {
      cityDirectory();
      return;
    }
    navigate({
      to: "/onboarding/$eventId",
      params: { eventId: event.ticketing_event_id },
    });
  }

  createEffect(() => {
    if (deepLinkHandled()) return;
    const meetupSlug = meetupSlugFromLocation(window.location.pathname, window.location.search);
    if (meetupSlug) {
      setDeepLinkHandled(true);
      navigate({ to: "/meetup/$slug", params: { slug: meetupSlug } });
      return;
    }
    if (events.state !== "ready" || !events()?.data.length) return;

    const params = new URLSearchParams(window.location.search);
    const eventId = resolveEventHint(params.get("event"));
    if (!eventId) return;

    const match = events()!.data.find((e) => e.ticketing_event_id === eventId);
    if (!match) return;

    setDeepLinkHandled(true);
    void onSelectEvent(match);
  });

  return (
    <section class="card">
      <p class="status-chip">Signed in</p>
      <Show when={!pass()}>
        <h1>Meetups</h1>
        <p>
          City nights are free. Register for the door QR here. Directory and conference networking
          stay for ticket holders.
        </p>
        <Show when={meetupNights.error}>
          <div class="error-box">{(meetupNights.error as Error).message}</div>
        </Show>
        <button
          type="button"
          class="btn btn-primary"
          style={{ "margin-bottom": "16px", width: "100%" }}
          onClick={() => navigate({ to: "/meetup" })}
        >
          Browse city nights
        </button>
      </Show>
      <Show when={pass()}>
      <h1>Choose your conference</h1>
      <p>
        Pick a city to claim your ticket, or browse everyone who has opted in. Bought tickets for
        someone else? <a href="/tickets">Manage ticket names</a>. New here?{" "}
        <a href="/help#claim">How to claim your ticket</a>
        {" · "}
        <a href="/help">FAQ</a>.
      </p>
      <button
        type="button"
        class="btn btn-primary"
        style={{ "margin-bottom": "12px", width: "100%" }}
        onClick={() => navigate({ to: "/directory" })}
      >
        Browse the directory
      </button>
      <button
        type="button"
        class="btn btn-outline"
        style={{ "margin-bottom": "12px", width: "100%" }}
        onClick={() => navigate({ to: "/meetup" })}
      >
        City meetups
      </button>
      <button
        type="button"
        class="btn btn-outline"
        style={{ "margin-bottom": "16px", width: "100%" }}
        onClick={() => navigate({ to: "/tickets" })}
      >
        Manage my ticket orders
      </button>
      </Show>

      <Show when={me.error}>
        <div class="error-box">
          Could not load profile: {(me.error as Error).message}
          <br />
          <span class="muted">
            Zitadel login can succeed while deb-api still rejects the token until Fly secrets
            match the staging tenant. In local dev, leave <code>VITE_DEB_API_URL</code> empty so
            Vite proxies <code>/api</code> to staging (avoids CORS).
          </span>
        </div>
      </Show>
      <Show when={me.state === "ready"}>
        <div class="user-chip">
          <div class="user-chip-avatar" aria-hidden="true">
            {(me()!.data.email || me()!.data.zitadel_id || "?").charAt(0).toUpperCase()}
          </div>
          <span class="muted">
            Logged in as <strong>{me()!.data.email || me()!.data.zitadel_id}</strong>
          </span>
        </div>
        <button
          type="button"
          class="btn btn-outline"
          style={{ "margin-top": "12px", width: "100%" }}
          onClick={() => navigate({ to: "/me/edit" })}
        >
          {memberProfile.state === "ready" && hasMemberProfileContent(memberProfile()?.data)
            ? "Edit your profile"
            : "Set up your profile"}
        </button>
      </Show>

      <Show when={pass() && events.loading}>
        <p class="muted">Loading events…</p>
      </Show>
      <Show when={pass() && events.error}>
        <div class="error-box">Events: {(events.error as Error).message}</div>
      </Show>
      <Show when={pass() && events.state === "ready"}>
        <Show
          when={partitioned().current.length > 0}
          fallback={
            <p class="muted" style={{ "margin-top": "16px" }}>
              No upcoming conferences are open in Connect right now.
            </p>
          }
        >
          <ul class="event-list">
            <For each={partitioned().current}>
              {(event, index) => (
                <EventRow
                  event={event}
                  featured={index() === 0}
                  onSelect={onSelectEvent}
                />
              )}
            </For>
          </ul>
        </Show>

        <Show when={partitioned().past.length > 0}>
          <div class="past-events">
            <button
              type="button"
              class="past-events-toggle"
              aria-expanded={pastOpen()}
              onClick={() => setPastOpen((v) => !v)}
            >
              <span>Past conferences</span>
              <span class="muted">
                {partitioned().past.length} · {pastOpen() ? "Hide" : "Show"}
              </span>
            </button>
            <Show when={pastOpen()}>
              <ul class="event-list event-list--past">
                <For each={partitioned().past}>
                  {(event) => (
                    <EventRow event={event} status="Past" onSelect={onSelectEvent} />
                  )}
                </For>
              </ul>
            </Show>
          </div>
        </Show>
      </Show>
    </section>
  );
}
